/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef, useEffect } from 'react'
import { camelCase } from 'camel-case'
import { format } from 'date-fns'

import { ErrorBoundary } from 'react-error-boundary'

import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Fade from '@mui/material/Fade'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Toolbar from '@mui/material/Toolbar'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Slide from '@mui/material/Slide'

import { ResizableBox } from 'react-resizable'
import Draggable from 'react-draggable'
import UserIcon from 'src/components/ui/UserIcon'
import { createPortal } from 'react-dom'

import { Markdown } from '@react-email/components'
import {
  IChatMessage,
  useAssistantAction,
  useAssistantSelect,
} from 'src/hooks/useAssistant'
import SwitchTransition from 'react-transition-group/SwitchTransition'
import TextField from 'src/components/ui/text-field'
import { useLeftNavTheme } from 'src/context/leftNavThemeContext'

import { MarkdownStyles } from './markdownStyles'
import { PlanApprovalCard } from './PlanApprovalCard'

const assistantNoiseBackground =
  'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27 viewBox=%270 0 140 140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.72%27 numOctaves=%273%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27140%27 height=%27140%27 filter=%27url(%23n)%27 opacity=%270.18%27/%3E%3C/svg%3E")'

const assistantDockLightBackground = `${assistantNoiseBackground},
  radial-gradient(circle at 12% 14%, rgba(11, 214, 141, 0.115) 0, rgba(11, 214, 141, 0.075) 8%, rgba(11, 214, 141, 0) 20%),
  radial-gradient(circle at 86% 10%, rgba(34, 141, 255, 0.11) 0, rgba(34, 141, 255, 0.07) 9%, rgba(34, 141, 255, 0) 22%),
  radial-gradient(circle at 72% 88%, rgba(87, 78, 249, 0.075) 0, rgba(87, 78, 249, 0.05) 8%, rgba(87, 78, 249, 0) 20%),
  linear-gradient(180deg, #fbfbfd 0%, #f3f6fb 100%)`

const assistantDockBackgroundSize = '140px 140px, auto, auto, auto, auto'

const assistantWidgetShadow = '0 4px 12px rgba(34, 47, 62, 0.045)'
const assistantGlassInnerBorder = 'inset 0 0 0 1px rgba(255, 255, 255, 0.44)'

const assistantGlassPanelShadow = `${assistantWidgetShadow}, ${assistantGlassInnerBorder}`

const assistantPanelBorder = 'rgba(36, 50, 61, 0.08)'
const assistantPanelSurface = 'rgba(255, 255, 255, 0.72)'
const assistantPanelBackdrop = 'blur(18px) saturate(145%)'

const LoadingDots = () => {
  return (
    <Stack
      direction="row"
      spacing={0.2}
      alignItems="center"
      sx={{
        '@keyframes wave': {
          '0%, 60%, 100%': {
            transform: 'translateY(0)',
          },
          '30%': {
            transform: 'translateY(-8px)',
          },
        },
      }}
    >
      {[0, 1, 2].map((index) => (
        <Typography
          key={index}
          variant="body2"
          color="success.main"
          sx={{
            animation: 'wave 1.4s ease-in-out infinite',
            animationDelay: `${index * 0.2}s`,
            display: 'inline-block',
            fontSize: '20px',
            lineHeight: 1,
          }}
        >
          ●
        </Typography>
      ))}
    </Stack>
  )
}

const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  )
}

const formatDateSeparator = (date: Date): string => {
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (isSameDay(date, today)) {
    return 'Today'
  } else if (isSameDay(date, yesterday)) {
    return 'Yesterday'
  } else {
    return format(date, 'dd MMMM yyyy')
  }
}

// const StreamMessage = (props: { threadId: string }) => {
//   const { threadId } = props
//   const streamMessages = useAssistantSelect.streamMessages()
//   const currentMessage = streamMessages?.[threadId]

//   // const [text, setText] = React.useState(streamMessage?.text || message.text)

//   // React.useEffect(() => {
//   //   if (streamMessage?.text) {
//   //     setText(streamMessage.text)
//   //   }
//   // }, [streamMessage?.text])

//   if (!currentMessage) {
//     return <></>
//   }

//   return (
//     <MessageCard
//       message={currentMessage}
//       isLoading={!currentMessage.content.content}
//     />
//   )
// }

const SendButton = (props: {
  threadId: string
  disabled: boolean
  onSendMessage: () => void
}) => {
  const { threadId, disabled, onSendMessage } = props
  const streamMessageCancel = useAssistantAction.streamMessageCancel()

  // TODO: Remove
  const currentMessage = false
  if (currentMessage) {
    return (
      <IconButton
        sx={{
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          right: '5px',
          height: '42px',
        }}
        onClick={() => streamMessageCancel({ threadId })}
        color="secondary"
      >
        <Typography
          color={'secondary'}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <UserIcon fontSize={26} icon="ic:round-stop"></UserIcon>
        </Typography>
      </IconButton>
    )
  }

  return (
    <IconButton
      sx={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: '5px',
        height: '42px',
      }}
      onClick={onSendMessage}
      disabled={disabled}
      color="primary"
    >
      <Typography
        color={disabled ? 'secondary' : 'primary'}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <UserIcon fontSize={26} icon="iconamoon:send-light"></UserIcon>
      </Typography>
    </IconButton>
  )
}
export const AssinstantChat = () => {
  const assinstantWindow = useAssistantSelect.window()
  const sendMessage = useAssistantAction.sendMessage()
  const getMemoryThreadsPaginated =
    useAssistantAction.getMemoryThreadsPaginated()

  const getMemoryThreadsPaginatedStatus =
    useAssistantAction.getMemoryThreadsPaginated.status()

  const getMemoryThreadsItemMessages =
    useAssistantAction.getMemoryThreadsItemMessages()
  const getMemoryThreadsItemMessagesStatus =
    useAssistantAction.getMemoryThreadsItemMessages.status()

  const threads = useAssistantSelect.threads()
  const messages = useAssistantSelect.messages()

  const [defaultThread] = threads

  const isLoading =
    (getMemoryThreadsItemMessagesStatus.isPending ||
      getMemoryThreadsPaginatedStatus.isPending) &&
    messages.length === 0

  const isEmpty =
    getMemoryThreadsItemMessagesStatus.isFulfilled && messages.length === 0

  React.useEffect(() => {
    if (!threads.length) {
      getMemoryThreadsPaginated({
        page: 0,
        perPage: 1,
        sortDirection: 'ASC',
      })
    }
  }, [threads.length])

  React.useEffect(() => {
    if (defaultThread?.id) {
      getMemoryThreadsItemMessages({
        threadId: defaultThread.id,
        limit: 20,
      })
    }
  }, [defaultThread?.id])

  const closeWidow = useAssistantAction.closeWidow()
  const updateWindow = useAssistantAction.updateWindow()

  const [inputValue, setInputValue] = useState('')

  const [connectionStatus, setConnectionStatus] = useState<
    'connected' | 'connecting' | 'disconnected'
  >('connected')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)

  const setScrollContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (node && isFirstRender.current && messages.length > 0 && !isLoading) {
        node.scrollTop = node.scrollHeight
        isFirstRender.current = false
      }
      ;(
        scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>
      ).current = node
    },
    [messages.length, isLoading]
  )

  const scrollToBottom = (instant = false) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    if (!isFirstRender.current && messages.length > 0) {
      scrollToBottom()
    }
  }, [
    messages.length,
    messages[messages.length - 1]?.content,
    messages[messages.length - 1]?.pendingApproval,
  ])

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !defaultThread) return

    const userMessage = inputValue.trim()

    setInputValue('')
    sendMessage({ threadId: defaultThread?.id, text: userMessage })
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      e.stopPropagation()
      handleSendMessage()
    }
  }

  return (
    <Stack
      height={'100%'}
      direction={'column'}
      width={'100%'}
      position={'relative'}
    >
      <Toolbar
        className={
          !assinstantWindow.rightSideBar ? 'assistant-draggable-handle' : ''
        }
        variant="dense"
        sx={{
          position: 'absolute',
          backgroundColor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(5px)',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          top: 0,
          zIndex: 400,
          width: '100%',
          borderBottom: '1px solid',
          cursor: !assinstantWindow.rightSideBar ? 'grab' : 'default',
          borderColor: 'rgba(0,0,0,0.08)',
        }}
      >
        <Typography color="primary" sx={{ userSelect: 'none' }} pr={1} pt={1}>
          <UserIcon icon="mingcute:ai-fill" fontSize={18}></UserIcon>
        </Typography>
        <Typography sx={{ userSelect: 'none', fontSize: 16, fontWeight: 400 }}>
          Assistant
        </Typography>
        <Stack
          flex={1}
          flexDirection={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
        >
          {assinstantWindow.rightSideBar === true && (
            <IconButton
              size="small"
              sx={{ p: 1 }}
              onClick={() => updateWindow({ rightSideBar: false })}
            >
              <UserIcon
                icon="material-symbols:select-window-2-outline"
                fontSize="16px"
              />
            </IconButton>
          )}
          {assinstantWindow.rightSideBar === false && (
            <IconButton
              size="small"
              sx={{ p: 1 }}
              onClick={() => updateWindow({ rightSideBar: true })}
            >
              <UserIcon
                icon="streamline-flex:layout-right-sidebar-remix"
                fontSize="16px"
              />
            </IconButton>
          )}

          <IconButton size="small" sx={{ p: 1 }} onClick={() => closeWidow()}>
            <UserIcon icon="tabler:x" fontSize="16px" />
          </IconButton>
        </Stack>
      </Toolbar>

      <SwitchTransition mode="out-in">
        <Fade key={isLoading ? 'loading' : isEmpty ? 'empty' : 'messages'}>
          <Stack
            ref={setScrollContainerRef}
            display={'flex'}
            direction={'column'}
            spacing={3}
            pt={'62px'}
            pb={'48px'}
            mb={'22px'}
            height={'100%'}
            maxHeight={'100%'}
            sx={{ overflowY: 'auto' }}
          >
            {isEmpty && (
              <Stack height={'80%'} alignItems={'center'} spacing={4}>
                <Typography color="primary">
                  <UserIcon icon="humbleicons:ai" fontSize={52}></UserIcon>
                </Typography>
                <Typography variant="body2" textAlign={'center'} px={2}>
                  Assistant that can retrieve user info,
                  <br />
                  data search and analyze records, <br />
                  update or create objects, and generate links.
                </Typography>
              </Stack>
            )}
            {isLoading && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                height={'100%'}
                width="100%"
              >
                <CircularProgress size={25} />
              </Stack>
            )}
            {!isEmpty && (
              <>
                {messages.map((message, index) => {
                  const currentDate = new Date(message.createdAt)
                  const isValidCurrentDate = !isNaN(currentDate.getTime())
                  const previousMessage = index > 0 ? messages[index - 1] : null
                  const previousDate = previousMessage
                    ? new Date(previousMessage.createdAt)
                    : null
                  const showDateSeparator =
                    isValidCurrentDate &&
                    (!previousDate || !isSameDay(currentDate, previousDate))

                  return (
                    <React.Fragment key={message.id}>
                      {showDateSeparator && (
                        <Stack
                          width={'100%'}
                          alignItems={'center'}
                          justifyContent={'center'}
                          my={2}
                        >
                          <Chip
                            label={formatDateSeparator(currentDate)}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(0, 0, 0, 0.08)',
                              color: 'text.secondary',
                              fontWeight: 500,
                            }}
                          />
                        </Stack>
                      )}
                      <MessageCard
                        key={message.id}
                        message={message}
                        threadId={defaultThread?.id}
                      />
                    </React.Fragment>
                  )
                })}
                {/* {selectedThread && (
                  <StreamMessage threadId={selectedThread?.id} />
                )} */}
              </>
            )}
            <div ref={messagesEndRef} />
          </Stack>
        </Fade>
      </SwitchTransition>

      <Stack
        px={2}
        pb={'0.6em'}
        position={'absolute'}
        bottom={0}
        width={'100%'}
      >
        <Stack
          direction={'row'}
          spacing={1}
          alignItems={'center'}
          position={'relative'}
        >
          <TextField
            value={inputValue}
            autoFocus
            multiline
            fullWidth
            size="small"
            minRows={2}
            maxRows={4}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(5px)',
              borderRadius: '12px',
              position: 'relative',
            }}
            inputProps={{ sx: { mr: '32px' } }}
            onFocus={(event) => event.stopPropagation()}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={
              connectionStatus === 'connected'
                ? 'Ask a question...'
                : connectionStatus === 'connecting'
                ? 'Connection...'
                : 'Something went wrong...'
            }
            // disabled={}
          />
          {defaultThread && (
            <SendButton
              threadId={defaultThread?.id}
              disabled={!inputValue.trim()}
              onSendMessage={handleSendMessage}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}
function MessageCard({
  message,
  threadId,
}: {
  message: IChatMessage
  threadId?: string
}) {
  const isLoading = message.content === '' && !message.pendingApproval

  return (
    <Stack
      justifyContent={'center'}
      width={'100%'}
      px={3}
      alignItems={message.role === 'user' ? 'flex-end' : 'flex-start'}
    >
      <Stack
        borderRadius={'5px'}
        py={2}
        px={2}
        sx={{
          wordWrap: 'break-world',
          backgroundColor: message.role === 'user' ? '#EEEDFD' : 'common.white',
        }}
        rowGap={1}
        maxWidth={'90%'}
        minWidth={'100px'}
      >
        <Typography fontSize={11} fontWeight={500} textTransform="capitalize">
          {message.role === 'user' ? 'YOU' : 'ASSISTANT'}
        </Typography>

        {isLoading ? (
          <LoadingDots />
        ) : message.content ? (
          <Typography variant="body2">
            {message.role === 'user' ? (
              message.content
            ) : (
              <Markdown
                children={message.content || ''}
                markdownCustomStyles={MarkdownStyles}
              />
            )}
          </Typography>
        ) : null}
        {message.pendingApproval && threadId && (
          <PlanApprovalCard
            threadId={threadId}
            pendingApproval={message.pendingApproval}
          />
        )}
        <Stack direction="row" columnGap={2}>
          <Typography
            variant="caption"
            textAlign={message.role === 'user' ? 'right' : 'left'}
          >
            {new Date(message.createdAt).toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
          {message.streamingText && (
            <Typography
              fontSize={11}
              sx={{
                background: (theme) =>
                  `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {message.streamingText}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}

function clampModalPosition(
  x: number,
  y: number,
  width: number,
  height: number
): { x: number; y: number } {
  const maxX = window.innerWidth - width
  const maxY = window.innerHeight - height

  return {
    x: Math.min(Math.max(0, x), Math.max(0, maxX)),
    y: Math.min(Math.max(0, y), Math.max(0, maxY)),
  }
}

export const AssinstantWindow = () => {
  const elRef = useRef<HTMLDivElement | null>(null)
  if (!elRef.current) {
    elRef.current = document.createElement('div')
  }

  const refContainer = useRef<HTMLDivElement>(null)
  const refDropRightZone = useRef<HTMLDivElement>(null)
  const assinstantWindow = useAssistantSelect.window()
  const updateWindow = useAssistantAction.updateWindow()
  const { leftNavTheme } = useLeftNavTheme()
  const isGlassTheme = leftNavTheme === 'light'
  const [pos, setPos] = useState({ x: 100, y: 100 })
  const [size, setSize] = useState({ w: 100, h: 100 })
  const [rightSideBarInit, setRightSideBarInit] = React.useState(
    assinstantWindow.rightSideBar
  )

  React.useEffect(() => {
    if (assinstantWindow.rightSideBar === false) {
      setRightSideBarInit(false)
    } else {
      setTimeout(() => setRightSideBarInit(true), 1000)
    }
  }, [assinstantWindow.rightSideBar])

  const isDropZone = (x: number) =>
    innerWidth - (x + assinstantWindow.width / 2) - 60 < 0
  useEffect(() => {
    const el = elRef.current!
    document.body.appendChild(el)

    return () => {
      document.body.removeChild(el)
    }
  }, [])

  const handelUpdatePosition = (post: { x: number; y: number }) => {
    const adaptivePost = clampModalPosition(
      post.x,
      post.y,
      assinstantWindow.width,
      assinstantWindow.height
    )
    setPos(adaptivePost)
    updateWindow(adaptivePost)
  }
  const handelUpdateSize = (size: { w: number; h: number }) => {
    setSize(size)
    updateWindow({ width: size.w, height: size.h })
  }

  useEffect(() => {
    let timer: number | null = null

    function handleResize() {
      if (timer) clearTimeout(timer)
      timer = window.setTimeout(() => {
        setPos((p) =>
          clampModalPosition(
            p.x,
            p.y,
            assinstantWindow.width,
            assinstantWindow.height
          )
        )
        timer = null
      }, 1000)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('resize', handleResize)
    }
  }, [
    assinstantWindow.width,
    assinstantWindow.height,
    assinstantWindow.x,
    assinstantWindow.y,
  ])

  React.useEffect(() => {
    setPos((p) =>
      clampModalPosition(
        assinstantWindow.x,
        assinstantWindow.y,
        assinstantWindow.width,
        assinstantWindow.height
      )
    )
    setSize({ w: assinstantWindow.width, h: assinstantWindow.height })
  }, [assinstantWindow.open])

  if (!assinstantWindow.open) return null

  if (rightSideBarInit && assinstantWindow.rightSideBar) {
    return createPortal(
      <Stack
        ref={refContainer}
        sx={{
          position: 'fixed',
          zIndex: 10000,
          top: 0,
          right: 0,
          width: '400px',
          pl: 6,
          backgroundColor: isGlassTheme ? '#F7F8FB' : '#F6F6F6',
          backgroundImage: isGlassTheme
            ? assistantDockLightBackground
            : undefined,
          backgroundBlendMode: isGlassTheme
            ? 'soft-light, normal, normal, normal, normal'
            : undefined,
          backgroundSize: isGlassTheme
            ? assistantDockBackgroundSize
            : undefined,
          py: 6,
          height: '100vh',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderBottomRightRadius: 0,
            borderTopRightRadius: 0,
            border: '1px solid',
            borderColor: assistantPanelBorder,
            boxShadow: isGlassTheme
              ? assistantGlassPanelShadow
              : assistantWidgetShadow,
            backgroundColor: isGlassTheme
              ? assistantPanelSurface
              : 'background.paper',
            backdropFilter: isGlassTheme ? assistantPanelBackdrop : undefined,
            WebkitBackdropFilter: isGlassTheme
              ? assistantPanelBackdrop
              : undefined,
            width: '100%',
            height: '100%',
          }}
        >
          <AssinstantChat />
        </Paper>
      </Stack>,
      elRef.current
    )
  }

  return createPortal(
    <div
      ref={refContainer}
      style={{
        pointerEvents: 'none',
        position: 'fixed',
        zIndex: 10000,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
      }}
    >
      <Stack
        ref={refDropRightZone}
        sx={{
          position: 'fixed',
          right: 0,
          height: '100%',
          width: 0,
          transition: 'all 0.3s ease-in-out',
          border: '1px solid rgba(11,214,141,0.5)',

          background: `linear-gradient(90deg, rgba(11, 214, 141, 0.5) 0%, rgba(11, 214, 141, 1) 80%)`,
          opacity: 0,
        }}
      ></Stack>
      {/* @ts-ignore */}
      <Draggable
        handle=".assistant-draggable-handle"
        position={{
          x: assinstantWindow.rightSideBar ? innerWidth - 409.1 : pos.x,
          y: assinstantWindow.rightSideBar ? 0 : pos.y,
        }}
        onStart={() => {
          if (refContainer.current) {
            refContainer.current.style.pointerEvents = 'all'
          }
          if (refDropRightZone.current) {
            refDropRightZone.current.style.width = '70px'
            refDropRightZone.current.style.opacity = '0.5'
          }
        }}
        onDrag={(event, { x, y }) => {
          if (refDropRightZone.current) {
            if (isDropZone(x)) {
              refDropRightZone.current.style.opacity = '1'
              if (!assinstantWindow.rightSideBarHover) {
                updateWindow({ rightSideBarHover: true })
              }
            } else {
              refDropRightZone.current.style.opacity = '0.5'
              if (assinstantWindow.rightSideBarHover) {
                updateWindow({ rightSideBarHover: false })
              }
            }
          }
          //  handelUpdatePosition({ x, y })
        }}
        onStop={(event, { x, y }) => {
          handelUpdatePosition({ x, y })
          if (refContainer.current) {
            refContainer.current.style.pointerEvents = 'none'
          }
          if (refDropRightZone.current) {
            if (isDropZone(x)) {
              updateWindow({ rightSideBarHover: false, rightSideBar: true })
            }
            refDropRightZone.current.style.width = '0px'
            refDropRightZone.current.style.opacity = '0'
          }
        }}
      >
        <Stack
          sx={{
            width: assinstantWindow.rightSideBar ? 400 : size.w,
            height: assinstantWindow.rightSideBar ? '100vh' : size.h,
            transition:
              rightSideBarInit !== assinstantWindow.rightSideBar
                ? 'all 0.3s ease-in-out'
                : undefined,
            py: assinstantWindow.rightSideBar ? 6 : 0,
            pl: assinstantWindow.rightSideBar ? 6 : 0,
            ...(assinstantWindow.rightSideBar
              ? {
                  backgroundColor: isGlassTheme ? '#F7F8FB' : undefined,
                  backgroundImage: isGlassTheme
                    ? assistantDockLightBackground
                    : undefined,
                  backgroundBlendMode: isGlassTheme
                    ? 'soft-light, normal, normal, normal, normal'
                    : undefined,
                  backgroundSize: isGlassTheme
                    ? assistantDockBackgroundSize
                    : undefined,
                }
              : {}),
          }}
        >
          <Paper
            elevation={0}
            sx={{
              pointerEvents: 'all',
              zIndex: 10000,
              border: '1px solid',
              borderColor: assistantPanelBorder,
              backdropFilter: isGlassTheme
                ? assistantPanelBackdrop
                : 'blur(10px)',
              WebkitBackdropFilter: isGlassTheme
                ? assistantPanelBackdrop
                : 'blur(10px)',
              backgroundColor: isGlassTheme
                ? assistantPanelSurface
                : 'rgba(255,255,255,0.7)',
              boxShadow: isGlassTheme
                ? assistantGlassPanelShadow
                : assistantWidgetShadow,
              position: 'relative',
              borderBottomRightRadius: assinstantWindow.rightSideBar
                ? '0'
                : undefined,
              borderTopRightRadius: assinstantWindow.rightSideBar
                ? '0'
                : undefined,
              width: assinstantWindow.rightSideBar ? '100%' : size.w,
              height: assinstantWindow.rightSideBar ? '100%' : size.h,
              transition:
                rightSideBarInit !== assinstantWindow.rightSideBar
                  ? 'all 0.3s ease-in-out'
                  : undefined,
            }}
          >
            {assinstantWindow.rightSideBar ? (
              <AssinstantChat />
            ) : (
              <ResizableBox
                width={size.w}
                height={size.h}
                minConstraints={[350, 450]}
                onResizeStart={(e, { size }) => {
                  handelUpdateSize({ w: size.width, h: size.height })
                  if (refContainer.current) {
                    refContainer.current.style.pointerEvents = 'all'
                  }
                }}
                onResizeStop={(e, { size }) => {
                  handelUpdateSize({ w: size.width, h: size.height })
                  if (refContainer.current) {
                    refContainer.current.style.pointerEvents = 'none'
                  }
                }}
                onResize={(e, { size }) =>
                  handelUpdateSize({ w: size.width, h: size.height })
                }
                handle={
                  <Stack
                    position={'absolute'}
                    bottom={4}
                    right={4}
                    color="grey.600"
                    sx={{ cursor: 'nw-resize' }}
                  >
                    <UserIcon icon="lets-icons:resize-down-right-light" />
                  </Stack>
                }
              >
                <AssinstantChat />
              </ResizableBox>
            )}
          </Paper>
        </Stack>
      </Draggable>
    </div>,
    elRef.current
  )
}
